/**
 * The only module that talks HTTP to the XFORMA API (docs/api/R1A_ROUTES.md and
 * docs/api/M1_ROUTES.md), with api/more.ts.
 *
 * - The class holds the request path (`call`) and the methods the app shell and the first screen of
 *   หน้าแรก use; every other method lives in api/more.ts, a chunk that loads on first use (and is
 *   warmed by the app shell). Both are methods of the same `api` — see `loadApiMore` below.
 *
 * - Cookie session, `credentials: 'same-origin'` (the dev server proxies /v1 → :8787).
 * - Every mutation carries `X-CSRF-Token` (from GET /v1/session). A 403 CSRF_FAILED
 *   refreshes the token once and retries — nothing changed server-side on a CSRF failure.
 * - `fetch` is injectable so pure classes (saveController) can be unit-tested.
 */
import type { Scene } from '@xcreative/scene-core';
import type {
  FileCategory,
  AuditFilter,
  AuditConsoleQuery,
  ApiErrorBody,
  CreateDesignBody,
  Design,
  DesignDetail,
  DesignSummary,
  FontInfo,
  MeInfo,
  OfflineManifest,
  Paged,
  Providers,
  SessionInfo,
  TemplateListPage,
  TemplateListQuery,
  WorkspaceSummary,
  Customer,
  DesignKind,
  Folder,
  FolderTreeResponse,
  OrganizeBody,
  OrganizeFilter,
  Presence,
  WorkType,
  TaskSummary,
  HelpPatch,
  HelpState,
  HelpUserState,
  ApprovalRequest,
  ApprovalListQuery,
  ApprovalSummary,
  ApprovalTargetType,
  ApprovalItemStateView,
} from './types.js';
import type { more } from './more.js';
import { importWithReload, isOffline } from '../lib/lazy.js';

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: { path: string; message: string }[];
  readonly requestId: string | null;

  constructor(status: number, body: Partial<ApiErrorBody> | null, fallback: string) {
    super(body?.message || fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.code || `HTTP_${status}`;
    this.details = Array.isArray(body?.details) ? body.details : [];
    this.requestId = body?.requestId ?? null;
  }
}

/** The request never produced an HTTP response (offline, DNS, reset, CORS…). */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    // Thai for anyone who prints .message directly; the browser's own text stays on .cause.
    super('เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ตรวจสอบอินเทอร์เน็ตแล้วลองอีกครั้ง', { cause });
    this.name = 'NetworkError';
  }
}

/**
 * A file of the app (api/more.ts) could not be downloaded although the browser is online: the release
 * on the server has moved on and this tab was opened before it. lib/lazy.ts reloads the page once for
 * that; when the file is still missing afterwards, the person is asked to reload (lib/lazy.ts
 * `isChunkLoadError` knows this name).
 */
export class StaleReleaseError extends Error {
  constructor(cause: unknown) {
    super('มีเวอร์ชันใหม่ของแอป กรุณาโหลดหน้าใหม่แล้วลองอีกครั้ง', { cause });
    this.name = 'StaleReleaseError';
  }
}

/**
 * A GET whose answer arrived after the signed-in person (or the session) changed: it was asked for
 * by the previous session, so it is dropped — neither shown nor cached (app/session.tsx calls
 * `api.newSession()` on every sign-in, sign-out and account switch). Named like an aborted request,
 * because that is what it is: code that ignores cancelled requests ignores this one too.
 */
export class StaleSessionError extends Error {
  readonly stale = true;
  constructor() {
    super('บัญชีที่ใช้งานเปลี่ยนแล้ว คำตอบของคำขอเดิมจึงไม่ถูกแสดง');
    this.name = 'AbortError';
  }
}

export const isApiError = (e: unknown, code?: string): e is ApiError =>
  e instanceof ApiError && (code === undefined || e.code === code);

/** `?folderId=` of a create request: the result is filed in that folder (nothing when there is none). */
export const inFolder = (folderId: string | null | undefined): { folderId: string } | undefined => (folderId ? { folderId } : undefined);

export interface RequestOptions {
  body?: unknown;
  rawBody?: BodyInit;
  headers?: Record<string, string>;
  idempotencyKey?: string;
  ifMatch?: string;
  keepalive?: boolean;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** How to read a 2xx body. Default: JSON (or null for 204). */
  as?: 'json' | 'blob' | 'arrayBuffer' | 'text';
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  etag: string | null;
  /** Content-Disposition of a file response (DOCX/PPTX downloads), if any. */
  disposition: string | null;
}

/** `attachment; filename*=UTF-8''…` / `filename="…"` → the file name, if any. */
export function filenameFromDisposition(disposition: string | null): string | null {
  if (!disposition) return null;
  const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(disposition);
  if (star) {
    try {
      return decodeURIComponent(star[1]!.trim().replace(/^"|"$/g, ''));
    } catch {
      /* fall through */
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1]!.trim() : null;
}

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * The bootstrap request that index.html started before any script was loaded (see the build plugin
 * in apps/web/build/delivery.ts). Used once; anything unexpected (no promise, a failed request, a
 * body of another shape) yields null and the app asks the API itself.
 */
async function takeEarlyBootstrap(): Promise<{ session: SessionInfo; me: MeInfo | null } | null> {
  if (typeof window === 'undefined') return null;
  const holder = window as unknown as { __xcBoot?: Promise<unknown> | null };
  const early = holder.__xcBoot;
  holder.__xcBoot = null;
  if (!early) return null;
  try {
    const data = (await early) as { session?: SessionInfo; me?: MeInfo | null } | null;
    if (!data || typeof data.session !== 'object' || data.session === null || typeof data.session.authenticated !== 'boolean') return null;
    return { session: data.session, me: data.me ?? null };
  } catch {
    return null;
  }
}

/**
 * 'pwa' when the app runs installed (its own window, display-mode: standalone), else 'browser'.
 * Sent with every request as X-XC-Client so the audit console can say how a session connects
 * (ADR 0035) — a description, never a permission.
 */
function clientMode(): 'pwa' | 'browser' {
  try {
    if (typeof window === 'undefined') return 'browser';
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    return standalone ? 'pwa' : 'browser';
  } catch {
    return 'browser';
  }
}

/** `"rev-12"` → 12. */
export function parseRevisionEtag(etag: string | null): number | null {
  const m = etag ? /rev-(\d+)/.exec(etag) : null;
  return m ? Number(m[1]) : null;
}

export const revisionEtag = (revision: number) => `"rev-${revision}"`;

export interface ApiClientOptions {
  fetch?: FetchLike;
  base?: string;
  csrfToken?: string | null;
  /** Pauses before each automatic repeat of a `503 BUSY_RETRY` (ms); `[]` switches the repeats off. */
  busyBackoffMs?: number[];
}

/**
 * `503 BUSY_RETRY` with `Retry-After: 1` is the server saying "the database made this request give
 * way to another one on the same data; nothing was saved — send it again" (ADR 0029). The client
 * does that itself, a bounded number of times with a growing pause, when sending again cannot do
 * anything twice: a GET, or a mutation that carries an Idempotency-Key (the server answers a repeat
 * of a request it already carried out with the stored answer). Everything else is shown to the person.
 */
const BUSY_BACKOFF_MS = [300, 700, 1500];

export class ApiClient {
  /** @internal (api/more.ts) */
  readonly fetchImpl: FetchLike;
  readonly base: string;
  csrfToken: string | null;
  /** Called on any 401 so the app can show "session expired" (PRD §4.3). */
  onUnauthenticated: (() => void) | null = null;
  /** JSON GETs started in this turn of the event loop, by URL (see `call`). */
  private readonly inflight = new Map<string, Promise<Response>>();
  /** Bumped by `newSession()`: a GET answers only the session that asked for it (StaleSessionError). */
  private sessionEpoch = 0;

  /**
   * Another person / session from now on (sign-in, sign-out, account switch): answers to GETs that
   * were already on their way are dropped when they arrive, and no request is shared across the change.
   */
  newSession(): void {
    this.sessionEpoch++;
    this.inflight.clear();
  }
  private readonly busyBackoffMs: number[];

  constructor(opts: ApiClientOptions = {}) {
    this.fetchImpl = opts.fetch ?? ((input, init) => globalThis.fetch(input, init));
    this.base = opts.base ?? '/v1';
    this.csrfToken = opts.csrfToken ?? null;
    this.busyBackoffMs = opts.busyBackoffMs ?? BUSY_BACKOFF_MS;
  }

  url(path: string, query?: RequestOptions['query']): string {
    let url = path.startsWith('http') || path.startsWith(this.base + '/') ? path : this.base + path;
    if (query) {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
      const s = qs.toString();
      if (s) url += (url.includes('?') ? '&' : '?') + s;
    }
    return url;
  }

  async call<T>(method: string, path: string, opts: RequestOptions = {}, retriedCsrf = false, busyTries = 0): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = { Accept: 'application/json', 'X-XC-Client': clientMode(), ...opts.headers };
    let body: BodyInit | undefined;
    if (opts.rawBody !== undefined) body = opts.rawBody;
    else if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(opts.body);
    }
    if (MUTATING.has(method) && this.csrfToken) headers['X-CSRF-Token'] = this.csrfToken;
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    if (opts.ifMatch) headers['If-Match'] = opts.ifMatch;
    const askedIn = this.sessionEpoch;

    let res: Response;
    try {
      const url = this.url(path, opts.query);
      // Two parts of a page asking for the same JSON at the same moment (the menu's bell and the
      // page, two hooks of one screen mounting together) share ONE request; each reads its own copy
      // of the answer. "The same moment" is the same turn of the event loop and nothing longer: a
      // request made later — after a save, an upload, a live event — must never be answered by one
      // that was already on its way and may have read the data before that change.
      const shareable = method === 'GET' && !opts.signal && !opts.headers && !opts.ifMatch && (opts.as ?? 'json') === 'json';
      let pending = shareable ? this.inflight.get(url) : undefined;
      if (!pending) {
        pending = this.fetchImpl(url, {
          method,
          headers,
          body,
          credentials: 'same-origin',
          keepalive: opts.keepalive,
          signal: opts.signal,
        });
        if (shareable) {
          const mine = pending;
          this.inflight.set(url, mine);
          setTimeout(() => {
            if (this.inflight.get(url) === mine) this.inflight.delete(url);
          }, 0);
        }
      }
      const got = await pending;
      res = shareable && typeof got.clone === 'function' ? got.clone() : got;
    } catch (e) {
      if ((e as { name?: string })?.name === 'AbortError') throw e;
      throw new NetworkError(e);
    }
    // Asked for by the previous person / session: never handed to this one (not even its 401 or 403).
    const stale = askedIn !== this.sessionEpoch;
    if (stale && method === 'GET') throw new StaleSessionError();

    if (!res.ok) {
      let errBody: Partial<ApiErrorBody> | null = null;
      try {
        errBody = (await res.json()) as Partial<ApiErrorBody>;
      } catch {
        errBody = null;
      }
      const err = new ApiError(res.status, errBody, `HTTP ${res.status}`);
      if (err.status === 403 && err.code === 'CSRF_FAILED' && !retriedCsrf && MUTATING.has(method)) {
        await this.session().catch(() => undefined);
        return this.call<T>(method, path, opts, true, busyTries);
      }
      // The database asked for the request again (nothing was saved): repeat it when that is safe.
      const pause = this.busyBackoffMs[busyTries];
      const repeatable = method === 'GET' || !!opts.idempotencyKey;
      const stream = typeof ReadableStream !== 'undefined' && opts.rawBody instanceof ReadableStream;
      if (err.status === 503 && err.code === 'BUSY_RETRY' && pause !== undefined && repeatable && !stream && !opts.signal?.aborted && Number(res.headers.get('Retry-After') ?? '1') <= 2) {
        await new Promise<void>((resolve) => setTimeout(resolve, pause + Math.random() * pause * 0.3));
        if (!opts.signal?.aborted) return this.call<T>(method, path, opts, retriedCsrf, busyTries + 1);
      }
      if (err.status === 401 && !stale && path !== '/auth/password/login' && path !== '/auth/password/change') this.onUnauthenticated?.();
      throw err;
    }

    const etag = res.headers.get('ETag');
    let data: unknown = null;
    if (res.status !== 204) {
      switch (opts.as ?? 'json') {
        case 'blob':
          data = await res.blob();
          break;
        case 'arrayBuffer':
          data = await res.arrayBuffer();
          break;
        case 'text':
          data = await res.text();
          break;
        default: {
          const text = await res.text();
          try {
            data = text ? JSON.parse(text) : null;
          } catch {
            // e.g. the SPA's index.html from a mis-routed proxy: never the browser's SyntaxError text.
            throw new ApiError(res.status, { code: 'BAD_RESPONSE', message: '' }, 'BAD_RESPONSE');
          }
        }
      }
    }
    // The body may arrive after a change that the headers did not see.
    if (method === 'GET' && askedIn !== this.sessionEpoch) throw new StaleSessionError();
    return { data: data as T, status: res.status, etag, disposition: res.headers.get('Content-Disposition') };
  }

  /** @internal GET → the JSON body (also used by api/more.ts). */
  async get<T>(path: string, opts?: RequestOptions): Promise<T> {
    return (await this.call<T>('GET', path, opts)).data;
  }
  /** @internal POST → the JSON body (also used by api/more.ts). */
  async post<T>(path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
    return (await this.call<T>('POST', path, { ...opts, body: body ?? {} })).data;
  }

  // ---------- ศูนย์ช่วยเหลือ + onboarding (docs/api/HELP.md) ----------

  helpState(w: string): Promise<HelpState> {
    return this.get(`/workspaces/${enc(w)}/help/state`);
  }
  async patchHelp(body: HelpPatch): Promise<HelpUserState> {
    return (await this.call<HelpUserState>('PATCH', '/me/help', { body })).data;
  }
  /** Counters only (an article was opened / a search found nothing); never rejects, so it stays out of api/more.ts. */
  helpEvent(w: string, body: { view?: string; miss?: string }): Promise<void> {
    return this.post<void>(`/workspaces/${enc(w)}/help/events`, body).catch(() => undefined);
  }

  /** Never rejects (a cancel that fails is moot), so it stays out of api/more.ts. */
  async cancelPhotoAiJob(id: string): Promise<void> {
    await this.call('DELETE', `/photo-ai/jobs/${enc(id)}`).catch(() => undefined);
  }


  // ---------- identity (D01) ----------

  providers(): Promise<Providers> {
    return this.get('/auth/providers');
  }

  /** "เข้าสู่ระบบด้วย 13STORE": a full-page navigation (the API redirects to the platform and back). */
  thirteenStoreStartUrl(returnTo = '/'): string {
    return `${this.base}/auth/13store/start?return=${encodeURIComponent(returnTo)}`;
  }

  /** Not part of R1A_ROUTES.md (only `/auth/providers.google` is). Assumed start URL. */
  readonly googleStartUrl = '/v1/auth/google/start';

  async session(): Promise<SessionInfo> {
    const s = await this.get<SessionInfo>('/session');
    if (s.csrfToken) this.csrfToken = s.csrfToken;
    return s;
  }

  logout(): Promise<null> {
    return this.post('/logout');
  }

  me(): Promise<MeInfo> {
    return this.get('/me');
  }

  /**
   * Session + (when signed in) /me in ONE round trip (GET /v1/bootstrap, ADR 0042). On the first
   * call the answer usually exists already: index.html starts the request while this bundle is
   * still downloading (`window.__xcBoot`). An API older than the route answers 404 — then the
   * two classic requests are made instead.
   */
  async bootstrap(): Promise<{ session: SessionInfo; me: MeInfo | null }> {
    let data = await takeEarlyBootstrap();
    if (!data) {
      try {
        data = await this.get<{ session: SessionInfo; me: MeInfo | null }>('/bootstrap');
      } catch (e) {
        if (!(e instanceof ApiError) || e.status !== 404) throw e;
        const session = await this.session();
        return { session, me: session.authenticated ? await this.me() : null };
      }
    }
    if (data.session.csrfToken) this.csrfToken = data.session.csrfToken;
    return data;
  }

  /** "ตั้งเป็นหน้าหลัก" (a workspace id) or follow the department again (null). */
  async setHomeWorkspace(workspaceId: string | null): Promise<Pick<MeInfo, 'workspaces' | 'homeWorkspaceId' | 'homeWorkspaceSource'>> {
    return (await this.call<Pick<MeInfo, 'workspaces' | 'homeWorkspaceId' | 'homeWorkspaceSource'>>('PUT', '/me/home-workspace', { body: { workspaceId } })).data;
  }

  createWorkspace(name: string): Promise<WorkspaceSummary> {
    return this.post('/workspaces', { name });
  }

  // ---------- designs ----------

  listDesigns(w: string, q: { q?: string; trashed?: boolean; scope?: 'all' | 'mine' | 'shared'; cursor?: string | null; limit?: number; kind?: DesignKind } & OrganizeFilter = {}): Promise<Paged<DesignSummary>> {
    return this.get(`/workspaces/${enc(w)}/designs`, {
      query: { q: q.q, scope: q.scope, trashed: q.trashed ? 1 : undefined, cursor: q.cursor, limit: q.limit, kind: q.kind, folder: q.folder, deep: q.deep ? 1 : undefined, customer: q.customer, workType: q.workType },
    });
  }

  /** `folderId`: file the new design there (every create route takes it — docs/api/ORGANIZE.md §Creating inside a folder). */
  createDesign(w: string, body: CreateDesignBody, idempotencyKey: string, folderId?: string | null): Promise<Design> {
    return this.post(`/workspaces/${enc(w)}/designs`, body, { idempotencyKey, query: inFolder(folderId) });
  }

  getDesign(d: string): Promise<DesignDetail> {
    return this.get(`/designs/${enc(d)}`);
  }

  async trashDesign(d: string): Promise<void> {
    await this.call('DELETE', `/designs/${enc(d)}`);
  }

  restoreDesign(d: string): Promise<Design> {
    return this.post(`/designs/${enc(d)}/restore`);
  }

  /** The copy sits next to the original unless `folderId` names another folder. */
  duplicateDesign(d: string, title?: string, folderId?: string | null): Promise<Design> {
    return this.post(`/designs/${enc(d)}/duplicate`, title ? { title } : {}, { query: inFolder(folderId) });
  }

  /** `view: 'audience'` asks the server for the slides without speaker notes (B13). */
  async getScene(d: string, view?: 'audience'): Promise<{ scene: Scene; revision: number | null }> {
    const res = await this.call<Scene>('GET', `/designs/${enc(d)}/scene`, view ? { query: { view } } : {});
    return { scene: res.data, revision: parseRevisionEtag(res.etag) };
  }

  async releaseLease(d: string, leaseToken: string, keepalive = false): Promise<void> {
    await this.call('POST', `/designs/${enc(d)}/lease/release`, { body: { leaseToken }, keepalive });
  }

  /** URL for the CSV export (opened directly so the browser downloads it with the session cookie). */
  auditExportCsvUrl(w: string, filter: AuditFilter = {}): string {
    return this.url(`/workspaces/${enc(w)}/audit/export.csv`, filter as Record<string, string>);
  }

  /** The current view as CSV (a plain link: the browser downloads it with the session cookie). */
  auditEventsCsvUrl(w: string, q: AuditConsoleQuery): string {
    return this.url(`/workspaces/${enc(w)}/audit/console/events.csv`, q as Record<string, string>);
  }

  auditSessionsCsvUrl(w: string, q: AuditConsoleQuery): string {
    return this.url(`/workspaces/${enc(w)}/audit/console/sessions.csv`, q as Record<string, string>);
  }

  /**
   * Absolute URLs the API hands out (uploadUrl, download url) that point at a /v1 path are
   * turned into same-origin paths so they go through the dev proxy with our cookie + Origin.
   */
  sameOrigin(url: string): string {
    try {
      const u = new URL(url, globalThis.location?.href ?? 'http://localhost/');
      if (u.pathname.startsWith(this.base + '/')) return u.pathname + u.search;
    } catch {
      /* keep as is */
    }
    return url;
  }

  assetContentUrl(a: string): string {
    return this.url(`/assets/${enc(a)}/content`);
  }

  /**
   * A picture for an `<img>` that shows it small (a tile, a chat bubble): a WebP at most `w` px
   * wide instead of the original (ADR 0042 §9). The server answers with the original when the
   * picture is not wider than that, and with 404 for a file it cannot resize — pair it with
   * `smallPictureFallback` so the original is used then.
   */
  assetVariantUrl(a: string, w: 800 | 1600 = 800): string {
    return this.url(`/assets/${enc(a)}/content`, { w });
  }

  /** `onError` of an `<img>` that shows `assetVariantUrl`: the original, once. */
  smallPictureFallback(a: string): (event: { currentTarget: HTMLImageElement }) => void {
    return (event) => {
      const img = event.currentTarget;
      if (img.dataset.original === '1') return;
      img.dataset.original = '1';
      img.removeAttribute('srcset');
      img.src = this.assetContentUrl(a);
    };
  }

  /** Always a download (Content-Disposition: attachment), even for images and media. */
  assetDownloadUrl(a: string): string {
    return this.url(`/assets/${enc(a)}/content`, { download: 1 });
  }
  async updateFileCategory(w: string, id: string, body: { name?: string; color?: string | null; ord?: number; archived?: boolean }): Promise<FileCategory> {
    return (await this.call<FileCategory>('PATCH', `/workspaces/${enc(w)}/file-categories/${enc(id)}`, { body })).data;
  }

  /** PDF page picture for the viewer (server-rendered, version-pinned so it caches). */
  pdfPageUrl(a: string, page: number, width: 800 | 1600, version: number): string {
    return this.url(`/assets/${enc(a)}/pages/${page}`, { w: width, v: version });
  }

  /** The tab closed (also sent while the page unloads). */
  async officeWebClose(sessionId: string, keepalive = false): Promise<void> {
    await this.call('DELETE', `/office-web/${enc(sessionId)}`, { keepalive });
  }

  fonts(): Promise<{ items: FontInfo[] }> {
    return this.get('/fonts');
  }

  // ---------- templates, campaigns, CSV Lite ----------

  /**
   * One page of the template library as summaries (ADR 0042 §9): filtered, ordered and paged by
   * the server; covers are pictures (`thumbnailUrl`), no scene is sent.
   */
  templateList(q: TemplateListQuery = {}, signal?: AbortSignal): Promise<TemplateListPage> {
    return this.get('/templates', { query: { view: 'summary', ...q }, signal });
  }

  // =====================================================================================
  // M1 (docs/api/M1_ROUTES.md)
  // =====================================================================================

  /** GET any same-origin /v1 URL handed out by the API (manifest URLs, sceneUrl) as bytes. */
  async fetchBytes(url: string, signal?: AbortSignal): Promise<ArrayBuffer> {
    return (await this.call<ArrayBuffer>('GET', this.sameOrigin(url), { as: 'arrayBuffer', signal })).data;
  }

  offlineManifest(d: string, revision?: number): Promise<OfflineManifest> {
    return this.get(`/designs/${enc(d)}/offline-manifest`, { query: { revision } });
  }

  /** Picture of page n (token checked on every request): the PNG, or with `w` a WebP at most that wide. */
  publicSharePageUrl(token: string, n: number, w?: 800 | 1600): string {
    return this.url(`/s/${enc(token)}/pages/${n}`, w ? { w } : undefined);
  }

  /** PDF, only if the link allows download (else 403). */
  publicShareDownloadUrl(token: string): string {
    return this.url(`/s/${enc(token)}/download`);
  }

  // ---------- organisation (docs/api/ORGANIZE.md) ----------

  /** The workspace's ONE folder tree with counts per kind (one request, one SQL statement). */
  folders(w: string): Promise<FolderTreeResponse> {
    return this.get(`/workspaces/${enc(w)}/folders`);
  }

  async updateFolder(id: string, patch: { name?: string; color?: string | null; parentId?: string | null; customerId?: string | null; workTypeId?: string | null }): Promise<Folder> {
    return (await this.call<Folder>('PATCH', `/folders/${enc(id)}`, { body: patch })).data;
  }

  organize(w: string, body: OrganizeBody): Promise<{ updated: number }> {
    return this.post(`/workspaces/${enc(w)}/organize`, body);
  }

  customers(w: string, q: { q?: string; orgType?: string } = {}): Promise<{ items: Customer[] }> {
    return this.get(`/workspaces/${enc(w)}/customers`, { query: { q: q.q, orgType: q.orgType } });
  }

  workTypes(w: string, includeArchived = false): Promise<{ items: WorkType[] }> {
    return this.get(`/workspaces/${enc(w)}/work-types`, { query: { includeArchived: includeArchived ? 1 : undefined } });
  }

  heartbeat(w: string, body: { clientId: string; designId: string | null }): Promise<Presence> {
    return this.post(`/workspaces/${enc(w)}/presence`, body);
  }

  async leavePresence(w: string, clientId: string): Promise<void> {
    await this.call('POST', `/workspaces/${enc(w)}/presence/leave`, { body: { clientId }, keepalive: true });
  }

  taskSummary(w: string): Promise<TaskSummary> {
    return this.get(`/workspaces/${enc(w)}/tasks/summary`);
  }

  convertPreviewUrl(id: string, page: number): string {
    return `${this.base}/convert/jobs/${enc(id)}/preview/${page}`;
  }

  async savePdfEditDraft(a: string, version: number, draft: unknown, keepalive = false): Promise<{ saved: boolean; updatedAt: string | null }> {
    return (await this.call<{ saved: boolean; updatedAt: string | null }>('PUT', `/assets/${enc(a)}/pdf-edit/draft`, { body: { version, draft }, keepalive })).data;
  }

  // ---------- conversion and recognition (ADR 0050) ----------

  /** A Word / Excel / PowerPoint file (this version or an earlier one) as a PDF, made on the spot. */
  assetPdfCopyUrl(a: string, version?: number): string {
    return this.url(`/assets/${enc(a)}/pdf-copy`, version ? { v: String(version) } : undefined);
  }

  // ---------- ระบบอนุมัติงาน (docs/api/APPROVALS.md, ADR 0027) ----------

  approvals(w: string, q: ApprovalListQuery, signal?: AbortSignal): Promise<{ items: ApprovalRequest[]; tab: string; isAdmin: boolean }> {
    return this.get(`/workspaces/${enc(w)}/approvals`, { signal, query: { tab: q.tab, type: q.type || undefined, status: q.status || undefined, overdue: q.overdue ? 1 : undefined, q: q.q || undefined } });
  }

  approvalSummary(w: string): Promise<ApprovalSummary> {
    return this.get(`/workspaces/${enc(w)}/approvals/summary`);
  }

  /** Status chips of up to 200 work items of one type (one request per list). */
  async approvalStates(w: string, type: ApprovalTargetType, ids: string[], signal?: AbortSignal): Promise<Record<string, ApprovalItemStateView>> {
    if (!ids.length) return {};
    return (await this.get<{ items: Record<string, ApprovalItemStateView> }>(`/workspaces/${enc(w)}/approvals/states`, { signal, query: { type, ids: ids.join(',') } })).items;
  }

  /** Admin CSV of every request and event (optionally one item's): a download URL. */
  approvalCsvUrl(w: string, q: { type?: ApprovalTargetType; id?: string; from?: string; to?: string } = {}): string {
    return this.url(`/workspaces/${enc(w)}/approvals/export.csv`, q);
  }
}

type More = typeof more;
/** ApiClient also has every method of api/more.ts (installed below; the chunk loads on first use). */
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export interface ApiClient extends More {}

/** The methods that live in api/more.ts (test/apiMore.test.ts keeps this list equal to that module). */
export const API_MORE_METHODS: readonly (keyof More)[] = (
  "helpFeedback helpReport setHelpSettings emailStart emailVerify passwordLogin changePassword adminAccounts adminWorkspaces createAdminAccount resetAdminAccountPassword disableAdminAccount enableAdminAccount setAdminAccountPasswordLogin ssoStatus saveSsoMapping ssoPreview ssoPresetPreview ssoPresetApply ssoSync renameAdminAccount devMailbox workspaces convertWorkspace members inviteMember updateMember removeMember usage renameDesign translateStatus setTranslateEnabled translateEstimate translateSegments translateDesign aiImageStatus setAiImageSettings aiImageConsent aiImageGenerate aiImageEdit aiImageKeep aiImageProvenance aiWriteStatus setAiWriteSettings aiWriteConsent aiWriteStream translateTorDraft saveScene acquireLease heartbeatLease takeoverLease revisions revisionScene restoreRevision designHistory fileHistory auditLog auditVerify auditPointInTime auditConsoleMeta auditOverview auditEvents auditSessions auditRevokeSession auditItemViewers grants addGrant deleteGrant comments addComment resolveComment initUpload putUpload completeAsset getAsset renameAsset assetContent assetVariant assetThumbnail listAssets assetUsage deleteAsset listFiles filesSummary fileCategories addFileCategory setFileCategory setFileMaster bulkFileCategory fileChildren detachFile trashUnusedChildren productFiles linkProductFiles unlinkProductFile setFileProtection fileDetails pdfPages slideImages backgroundRemoval backgroundMask removeBackground photoStudioInfo savePhotoProject exportPhoto photoSubject photoAi photoInpaint photoUpscale photoAiJob photoAiJobResult pptxSlides starFile restoreFile purgeFile bulkFiles zipFiles myFilesOverview myFilesSearch moveToTeam placeFiles removePlacement collections createCollection collection updateCollection deleteCollection removeFromCollection pins pin filesAdminOverview dedupeFiles replaceFile replaceFileFromWordEditor restoreFileVersion officeEditStatus officeEditStart officeEditRevoke officeConfig officeWebOpen officeWebPing officeWebEnd officeNew fileLink people fileVisibility setFileVisibility folderVisibility setFolderVisibility shareFiles sendToCatalog listCanvaProjects clearCanvaProjects deleteCanvaProject linkCanvaProjectFile integrations googleDriveStatus googleDriveStart googlePicker driveSettings setDriveAutoSave setDriveFolder unsetDriveFolder driveSave driveFiles driveReturn identifyWordFile addSourceVersion sourceVersions exportCatalogSpec exportCatalogCompare exportOutline torWordPreview torWordApply googleDriveFiles googleDriveImport googleDriveDisconnect fontLicenses fontLicenseText brands createBrand updateBrand fontBytes preflight createExport getJob cancelJob download template instantiateTemplate fillTemplateFromProducts templateSets createCampaign createCsvJob publicSamples importPptxInfo importPptx importReport createPublication publications withdrawPublication releaseCheck issueRelease listReleases withdrawRelease createPublicationLink designLinks revokeShareLink revealShareLink publicShare createTemplate publishTemplate createTemplateLink templateLink instantiateTemplateLink catalogDictionary catalogItems createCatalogItem catalogItem catalogVersion updateCatalogVersion createCatalogChange submitCatalogVersion verifyFact publishCatalogVersion revokeCatalogVersion catalogImpact catalogPrices addCatalogPrice startCatalogImport commitCatalogImport createWorkGeneration bindings adoptBinding torDrafts createFolder deleteFolder folderStarter customerFolders folderMergeReport createCustomer customer updateCustomer deleteCustomer createWorkType updateWorkType presence tasks createTask task patchTask archiveTask addTaskLink removeTaskLink addTaskComment readGoogleSheet docLinks addDocLink renameDocLink updateDocLink removeDocLink refreshDocLink torDraftFromDocLink torDraftFromFile torFileDoc torFileSavePreview torFileSave torFilePull torFilePullApply torDriveDoc torDriveSavePreview torDriveSave torDrivePull torDrivePullApply createTorDraft torDraft saveTorBuilder torComparisonFiles attachTorComparison removeTorComparison setTorProduct patchTorDraft addTorClause patchTorClause deleteTorClause torClauseEvidence submitTorDraft reviewTorDraft torExportToFiles exportTorBundle exportTorDraft torLibrary importTorLibrary patchLibraryItem convertPdfDocx convertTor convertOcrDrive convertJobs convertJob cancelConvertJob downloadConvertJob pdfToolsConfig pdfInfo pdfEditPage pdfEditPreview pdfEditFind pdfEditDraft deletePdfEditDraft pdfArchival createPdfJob pdfJobs pdfJob cancelPdfJob downloadPdfJob exportTorPdf ocrText createScanSession scanSession joinScanSession scanSessionPages scanSessionDone closeScanSession scansFiled pdfExports pdfStamps createPdfStamp patchPdfStamp deletePdfStamp torTemplates torTemplate saveTorAsTemplate patchTorTemplate publishTorTemplate retireTorTemplate editTorTemplate saveTorTemplateVersion torTemplateDiff useTorTemplate exportTorTemplate torTemplateUpdate applyTorTemplateUpdate approvalHistory approvalRoutePreview approvalSettings saveApprovalSettings approvalRoutes saveApprovalRoute deleteApprovalRoute requestApproval approval decideApproval bulkApprove withdrawApproval commentOnApproval setApprovalDue"
).split(' ') as (keyof More)[];

let moreLoad: Promise<More> | null = null;
/**
 * api/more.ts, fetched once: on the first call of one of its methods, or earlier when the app shell
 * warms it (App.tsx). Like a page chunk (lib/lazy.ts): a file the old release had and the new one has
 * not reloads the page once. A failure that remains is retried by the next call and reported as a
 * stale release — or, offline, like a failed request. `load` is for tests.
 */
export function loadApiMore(load: () => Promise<{ more: More }> = () => import('./more.js')): Promise<More> {
  moreLoad ??= importWithReload(load).then(
    (m) => m.more,
    (e: unknown) => {
      moreLoad = null;
      throw isOffline() ? new NetworkError(e) : new StaleReleaseError(e);
    },
  );
  return moreLoad;
}

for (const name of API_MORE_METHODS) {
  Object.defineProperty(ApiClient.prototype, name, {
    configurable: true,
    writable: true,
    value(this: ApiClient, ...args: unknown[]) {
      return loadApiMore().then((m) => (m[name] as (...a: unknown[]) => Promise<unknown>).apply(this, args));
    },
  });
}

/** @internal Path segment encoding (also used by api/more.ts). */
export const enc = encodeURIComponent;

/** App-wide client (browser fetch). Tests construct their own with a mocked fetch. */
export const api = new ApiClient();

/** Thai, user-facing description of an API/network failure. */
/** Local copy per error code: used when the server sent no (Thai) message, e.g. a proxy error page. */
const KNOWN: Record<string, string> = {
  UNAUTHENTICATED: 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบอีกครั้ง',
  FORBIDDEN: 'คุณไม่มีสิทธิ์ทำรายการนี้',
  CSRF_FAILED: 'การยืนยันความปลอดภัยล้มเหลว กรุณารีเฟรชหน้า',
  RESOURCE_NOT_FOUND: 'ไม่พบข้อมูล หรือคุณไม่มีสิทธิ์เข้าถึง',
  REVISION_CONFLICT: 'มีฉบับใหม่กว่าบนเซิร์ฟเวอร์',
  IDEMPOTENCY_MISMATCH: 'คำขอซ้ำกับคำขอก่อนหน้าแต่เนื้อหาต่างกัน',
  INVALID_STATE: 'สถานะปัจจุบันไม่รองรับการทำรายการนี้',
  PAYLOAD_TOO_LARGE: 'ข้อมูลมีขนาดใหญ่เกินกำหนด',
  VALIDATION_FAILED: 'ข้อมูลไม่ถูกต้อง',
  UNSUPPORTED_MEDIA_TYPE: 'ไม่รองรับชนิดไฟล์นี้สำหรับการใช้งานนี้',
  SEPARATION_OF_DUTIES: 'ผู้ตรวจต้องไม่ใช่ผู้เขียนร่าง',
  UNRESOLVED_ISSUES: 'ยังมีปัญหาระดับ “ต้องแก้” ค้างอยู่ จึงอนุมัติไม่ได้',
  NOT_AN_APPROVER: 'คุณไม่ได้เป็นผู้อนุมัติของคำขอนี้ในขั้นนี้',
  SELF_APPROVAL_FORBIDDEN: 'อนุมัติงานของตัวเองไม่ได้',
  APPROVAL_STALE: 'งานถูกแก้ไขหลังส่งขออนุมัติ ต้องส่งคำขอใหม่',
  APPROVAL_PENDING: 'งานนี้รออนุมัติอยู่แล้ว',
  APPROVAL_REQUIRED: 'ต้องได้รับอนุมัติก่อนจึงจะทำรายการนี้ได้',
  NO_ELIGIBLE_APPROVER: 'ยังไม่มีผู้อนุมัติสำหรับงานนี้ — เลือกผู้อนุมัติเพิ่ม',
  COMMENT_REQUIRED: 'ต้องระบุเหตุผล',
  DRAFT_EXISTS: 'สินค้านี้มีฉบับร่างอยู่แล้ว เปิดฉบับร่างเดิมเพื่อแก้ไขต่อ',
  ALREADY_MEMBER: 'อีเมลนี้เป็นสมาชิกแล้ว',
  PERSONAL_WORKSPACE: 'พื้นที่ส่วนตัวเชิญสมาชิกไม่ได้ สร้างทีมก่อน',
  SELF_HOSTED_ONLY: 'ใช้ได้เฉพาะเซิร์ฟเวอร์ขององค์กร (self-hosted)',
  ALREADY_TEAM: 'เวิร์กสเปซนี้เป็นทีมอยู่แล้ว',
  LEASE_REQUIRED: 'สิทธิ์แก้ไขหมดอายุ',
  LEASE_HELD: 'มีแท็บ/ผู้ใช้อื่นกำลังแก้ไข',
  PRECONDITION_REQUIRED: 'ต้องระบุฉบับที่แก้ไข',
  QUOTA_EXCEEDED: 'ใช้โควตาครบแล้ว',
  RATE_LIMITED: 'มีคำขอมากเกินไป กรุณารอสักครู่',
  POLICY_LOCKED: 'แม่แบบล็อกองค์ประกอบนี้ไว้ แก้ไขไม่ได้',
  SEAT_LIMIT: 'ที่นั่งผู้แก้ไขเต็มตามแผนแล้ว',
  EXPORT_PIXEL_LIMIT: 'ขนาดไฟล์ส่งออกเกินขีดจำกัดพิกเซล',
  PASSWORD_CHANGE_REQUIRED: 'ต้องตั้งรหัสผ่านใหม่ก่อนเริ่มใช้งาน',
  ACCOUNT_DISABLED: 'บัญชีนี้ถูกระงับการใช้งาน กรุณาติดต่อผู้ดูแลระบบ',
  ACCOUNT_EXISTS: 'มีบัญชีที่ใช้อีเมลนี้อยู่แล้ว',
  INSTANCE_ADMIN_ONLY: 'เฉพาะผู้ดูแลระบบของเซิร์ฟเวอร์นี้เท่านั้น',
  INSTANCE_ADMIN_PROTECTED: 'ระงับบัญชีผู้ดูแลระบบไม่ได้',
  INVALID_CREDENTIALS: 'อีเมลหรือรหัสผ่านไม่ถูกต้อง',
  GOOGLE_NOT_CONNECTED: 'ยังไม่ได้เชื่อมต่อ Google Drive',
  GOOGLE_RECONNECT_REQUIRED: 'การเชื่อมต่อ Google Drive หมดอายุหรือถูกถอน กรุณาเชื่อมต่อใหม่',
  GOOGLE_FORBIDDEN: 'Google Drive ปฏิเสธการเข้าถึง (สิทธิ์ไม่พอหรือเกินโควตา API)',
  GOOGLE_FILE_NOT_FOUND: 'ไม่พบไฟล์นี้ใน Google Drive',
  GOOGLE_TIMEOUT: 'Google Drive ตอบช้าเกินไป ลองอีกครั้ง',
  GOOGLE_UPSTREAM: 'ติดต่อ Google Drive ไม่สำเร็จ ลองอีกครั้งภายหลัง',
};

/** Codes whose local wording is better than the server's short one (it names the next step). */
const PREFER_LOCAL = new Set(['UNAUTHENTICATED', 'RESOURCE_NOT_FOUND']);
const THAI = /[\u0E00-\u0E7F]/;

/** Field names a validation error may point at, as the forms label them. */
const FIELD_LABELS: Record<string, string> = {
  email: 'อีเมล', password: 'รหัสผ่าน', newPassword: 'รหัสผ่านใหม่', currentPassword: 'รหัสผ่านปัจจุบัน', title: 'ชื่อ', name: 'ชื่อ',
  nameTh: 'ชื่อภาษาไทย', sku: 'รหัสสินค้า (SKU)', quantity: 'จำนวน', unit: 'หน่วย', amount: 'ราคา', validFrom: 'วันเริ่ม', validTo: 'วันสิ้นสุด',
  text: 'ข้อความ', threshold: 'ค่าที่กำหนด', note: 'หมายเหตุ', reason: 'เหตุผล', role: 'บทบาท', filename: 'ชื่อไฟล์', byteSize: 'ขนาดไฟล์',
  location: 'สถานที่', objective: 'วัตถุประสงค์', expiresAt: 'วันหมดอายุ', displayName: 'ชื่อที่แสดง',
};

/** One JSON-schema (ajv) complaint in Thai, naming the field as the form does — never a JSON pointer. */
export function validationText(detail: { path: string; message: string }): string {
  const key = detail.path.split('/').filter((p) => p && !/^\d+$/.test(p)).at(-1) ?? '';
  const label = FIELD_LABELS[key] ? `“${FIELD_LABELS[key]}”` : 'ข้อมูลบางช่อง';
  const m = detail.message;
  let r: RegExpExecArray | null;
  if (/must have required property/.test(m)) return `ยังไม่ได้กรอก${label}`;
  if (/must be (number|integer)/.test(m)) return `${label}ต้องเป็นตัวเลข`;
  if (/must be string/.test(m)) return `${label}ต้องเป็นข้อความ`;
  if ((r = /must NOT have more than (\d+) (characters|items)/.exec(m))) return `${label}ยาวเกิน ${r[1]} ${r[2] === 'items' ? 'รายการ' : 'ตัวอักษร'}`;
  if ((r = /must NOT have fewer than (\d+) (characters|items)/.exec(m))) return `${label}ต้องมีอย่างน้อย ${r[1]} ${r[2] === 'items' ? 'รายการ' : 'ตัวอักษร'}`;
  if ((r = /must be >= (-?[\d.]+)/.exec(m))) return `${label}ต้องไม่น้อยกว่า ${r[1]}`;
  if ((r = /must be <= (-?[\d.]+)/.exec(m))) return `${label}ต้องไม่เกิน ${r[1]}`;
  if (/must match format/.test(m) || /must match pattern/.test(m) || /invalid email/.test(m)) return `${label}มีรูปแบบไม่ถูกต้อง`;
  if (/must be equal to one of the allowed values|must be equal to constant/.test(m)) return `${label}เป็นค่าที่ระบบไม่รองรับ`;
  if (/must NOT have additional properties/.test(m)) return 'มีข้อมูลที่ระบบไม่รู้จักปนมา กรุณารีเฟรชหน้าแล้วลองอีกครั้ง';
  return THAI.test(m) ? m : `ตรวจสอบ${label}อีกครั้ง`;
}

/**
 * The Thai sentence for a failure, from its parts (shared by describeError and the save status).
 * Server messages are Thai and specific (they name the plan, the reason, the fix) so they win;
 * the local table covers bodies that were missing or English; 5xx and proxy errors never show
 * "HTTP 502" but say the server is temporarily unavailable, with the request reference.
 */
export function friendlyErrorText(code: string, message: string, status: number | null, details: { path: string; message: string }[] | null, requestId: string | null = null): string {
  const ref = requestId ? ` (รหัสอ้างอิง ${requestId.slice(0, 8)})` : '';
  if (code === 'NETWORK') return 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ตรวจสอบอินเทอร์เน็ตแล้วลองอีกครั้ง';
  if (code === 'BAD_RESPONSE') return 'เซิร์ฟเวอร์ตอบกลับในรูปแบบที่ไม่ถูกต้อง ระบบอาจกำลังอัปเดต กรุณารีเฟรชหน้าแล้วลองอีกครั้ง';
  const http = /^HTTP_(\d{3})$/.exec(code);
  const st = status ?? (http ? Number(http[1]) : null);
  if ((st !== null && st >= 500) || code === 'INTERNAL') {
    return `เซิร์ฟเวอร์ขัดข้องชั่วคราว${st ? ` (รหัส ${st})` : ''} กรุณาลองอีกครั้งในอีกสักครู่ หากยังไม่ได้ให้แจ้งผู้ดูแลระบบ${ref}`;
  }
  if (code === 'VALIDATION_FAILED') {
    // The generic "ข้อมูลไม่ถูกต้อง" comes from schema validation: say which field and what is wrong.
    const first = details?.[0];
    if (first && (!THAI.test(message) || message === 'ข้อมูลไม่ถูกต้อง')) return `ข้อมูลไม่ถูกต้อง: ${validationText(first)}`;
  }
  const server = message && THAI.test(message) && !PREFER_LOCAL.has(code) ? message : null;
  if (server) return server;
  if (KNOWN[code]) return KNOWN[code]!;
  if (http) return `คำขอไม่สำเร็จ (รหัส ${http[1]}) กรุณาลองอีกครั้ง${ref}`;
  return THAI.test(message) ? message : `ทำรายการไม่สำเร็จ กรุณาลองอีกครั้ง${ref}`;
}

export function describeError(e: unknown): string {
  if (e instanceof NetworkError) return friendlyErrorText('NETWORK', '', null, null);
  if (e instanceof ApiError) return friendlyErrorText(e.code, e.message, e.status, e.details, e.requestId);
  // Local errors (upload checks, editor commands) already speak Thai; a browser's English
  // ("Failed to fetch", a TypeError…) never reaches the user as is.
  if (e instanceof Error && THAI.test(e.message)) return e.message;
  if (e instanceof Error && /fetch|network|load failed/i.test(e.message)) return friendlyErrorText('NETWORK', '', null, null);
  return 'เกิดข้อผิดพลาดที่ไม่คาดคิด กรุณาลองอีกครั้ง';
}
